import { PAYMENTS_ENABLED } from "../../config/payments";
import { CHECKOUT_MARKER_SLACK_MS, checkoutInFlight, currentGuestIdentity, isCheckoutScope, writeCheckoutMarker, type CheckoutScope } from "../../lib/guestSession";
import { checkoutRedirectTarget, leaveForCheckout } from "./checkoutRedirect";
import { isCheckoutRef, keepCheckoutRecord, readCheckoutRecord } from "./checkoutRecord";

/**
 * STARTING STRIPE-HOSTED CHECKOUT FROM A BOOKING (payments prep, rc.13 plan
 * §2.4 and §2.5; design §2.1, §2.7, the Web Director's Q-A and Q-B). Pure
 * decisions plus the one leave step; the booking flows call them once
 * booking-v1 1.20 lets a document select the fields.
 */

/** `nextClientAction` as payments v1.1.0 shapes it (rev 3 item 9), unpinned. */
export interface CompletePaymentAction {
  readonly type: string;
  readonly checkoutUrl?: unknown;
  readonly checkoutRef?: unknown;
  /** The checkout's expiry (rev 3: `awaitingDeadline`). */
  readonly awaitingDeadline?: unknown;
}

export interface CheckoutStart {
  readonly url: string;
  readonly ref: string;
  readonly expiresAt: number;
  /** What it is for (W4 F1): the booking's professional, or the /a appointment. */
  readonly scope: CheckoutScope;
}

/**
 * A booking's "pay now", or null. Every part must hold: COMPLETE_PAYMENT, a
 * guard-accepted Checkout URL, a well-formed reference, a future expiry, and
 * a well-formed scope (W4 F1). Anything less is not a checkout Book may
 * start (the caller shows its failure).
 */
export function checkoutStartFrom(action: CompletePaymentAction | null | undefined, scope: unknown, now: number = Date.now()): CheckoutStart | null {
  if (!action || action.type !== "COMPLETE_PAYMENT") return null;
  const url = checkoutRedirectTarget(action.checkoutUrl);
  const ref = isCheckoutRef(action.checkoutRef) ? action.checkoutRef : null;
  const expiresAt = typeof action.awaitingDeadline === "string" ? Date.parse(action.awaitingDeadline) : Number.NaN;
  if (url === null || ref === null || !Number.isFinite(expiresAt) || expiresAt <= now) return null;
  if (!isCheckoutScope(scope)) return null;
  return { url, ref, expiresAt, scope };
}

/**
 * LEAVE FOR CHECKOUT, from the continuation of a person's press (never a
 * load or `pageshow`). The tab's record (H3) and the identity's marker
 * (§2.1) are written only once the guard accepts, just before the
 * navigation; a refusal writes neither. False while the gate is closed.
 *
 * `stillStarter` (W4 F2, the security auditor): asked last, just before
 * anything is written. False means the identity is no longer the one that
 * started the checkout: no record, no marker, no navigation.
 */
export function beginHostedCheckout(start: CheckoutStart, now: number = Date.now(), stillStarter: () => boolean = () => true): boolean {
  if (!PAYMENTS_ENABLED) return false;
  return leaveForCheckout(start.url, () => {
    if (!stillStarter()) return false;
    keepCheckoutRecord(start.ref, start.scope, start.expiresAt + CHECKOUT_MARKER_SLACK_MS, now);
    writeCheckoutMarker(start.expiresAt, start.scope, now);
    return true;
  });
}

/** Moved to ./capabilities (the booking flow imports it on every build); re-exported for the payments modules. */
export { clientCapabilities } from "./capabilities";

/**
 * A FRESH-LOAD REVIEW WHILE A CHECKOUT MAY STILL BE OPEN (design §2.7; Q-A):
 * a new attempt is blocked only by a live marker for THIS professional. The
 * reference from this tab's record lets the page ask instead of guessing.
 */
export function checkoutBlockFor(
  professionalId: string,
  now: number = Date.now()
): { readonly blocked: boolean; readonly ref: string | null; readonly identity: number | null } {
  // The identity this decision is for (security audit H-2): a press acts only while it is still current.
  const identity = currentGuestIdentity();
  if (!checkoutInFlight(professionalId, now)) return { blocked: false, ref: null, identity };
  const record = readCheckoutRecord(now);
  // Only a record of THIS professional AND this identity names the checkout
  // that blocks (security code review of §2.4 (a), (b)); otherwise the block
  // stands with the honest message, and nothing is asked with a stale ref.
  // A booking's record only (W4 F1): an /a checkout never names Review's block.
  const ours = record !== null && record.scope.kind === "p" && record.scope.id === professionalId && record.identity === identity;
  return { blocked: true, ref: ours ? record.ref : null, identity };
}

